<template>
	<modal size="medium" @close="handleClose">
		<template slot="header">
			<h3>Bulk Commission Update</h3>
		</template>

		<template slot="body">
			<div class="alert alert-info mb-3 commission-info">
				<i class="material-icons md-sm mr-2">info</i>
				Updating commission for <strong>{{ selectedCount }}</strong> selected product(s)
			</div>

			<form @submit.prevent="submit">
				<div class="form-group">
					<label>Type</label>
					<select class="form-control" v-model="form.type">
						<option value="percentage">Percentage</option>
						<option value="fixed">Fixed</option>
					</select>
					<div v-if="errors.type" class="text-danger small">{{ errors.type }}</div>
				</div>

				<div class="form-group">
					<label>Value</label>
					<input
						type="number"
						class="input form-control"
						min="0"
						step="0.01"
						v-model.number="form.value"
					/>
					<div v-if="errors.value" class="text-danger small">{{ errors.value }}</div>
				</div>

				<div class="form-group" v-if="form.type === 'percentage'">
					<label>Minimum Commission (optional)</label>
					<input
						type="number"
						class="input form-control"
						min="0"
						step="0.01"
						v-model.number="form.minimum_commission"
					/>
					<div v-if="errors.minimum_commission" class="text-danger small">{{ errors.minimum_commission }}</div>
				</div>

				<div class="form-group">
					<label>Impact</label>
					<select class="form-control" v-model="form.impact">
						<option value="affect_selling_price">Affects selling price</option>
						<option value="not_affect_selling_price">Does not affect selling price</option>
					</select>
					<div v-if="errors.impact" class="text-danger small">{{ errors.impact }}</div>
				</div>
			</form>
		</template>

		<template slot="footer">
			<button type="button" class="btn btn-light" @click="handleClose" :disabled="isSubmitting">Cancel</button>
			<button type="button" class="btn btn-primary" :class="{ 'is-loading': isSubmitting }" @click="submit">Submit</button>
		</template>
	</modal>
</template>

<script>
import Modal from '@/components/Modal.vue';
import { bulkUpdateCommission } from '@/api/admin';
import { duplicateVar, getAxiosErrorMessage } from '@/lib/helper';
import { DEFAULT_COMMISSION_CONFIG } from '@/lib/statics';

const COMMISSION_TYPES = ['percentage', 'fixed'];
const IMPACT_MODES = ['affect_selling_price', 'not_affect_selling_price'];

export default {
	name: 'BulkCommissionModal',
	components: { Modal },
	props: {
		selectedProducts: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			form: duplicateVar(DEFAULT_COMMISSION_CONFIG),
			errors: {},
			isSubmitting: false,
		};
	},
	computed: {
		selectedCount() {
			return this.selectedProducts.length;
		},
	},
	watch: {
		'form.type': function (next) {
			if (next !== 'percentage') {
				this.form.minimum_commission = null;
			}
		},
	},
	methods: {
		handleClose() {
			this.$emit('close');
		},
		validate() {
			this.errors = {};
			if (!COMMISSION_TYPES.includes(this.form.type)) {
				this.errors.type = 'Type is required';
			}
			if (this.form.value === '' || this.form.value === null || Number(this.form.value) < 0) {
				this.errors.value = 'Value must be a number greater than or equal to 0';
			}
			if (this.form.type === 'percentage' && this.form.minimum_commission !== null && Number(this.form.minimum_commission) < 0) {
				this.errors.minimum_commission = 'Minimum commission must be 0 or greater';
			}
			if (!IMPACT_MODES.includes(this.form.impact)) {
				this.errors.impact = 'Impact is required';
			}
			return Object.keys(this.errors).length === 0;
		},
		submit() {
			if (!this.validate()) {
				return;
			}
			const minimum = this.form.type === 'percentage'
				&& this.form.minimum_commission !== null
				&& this.form.minimum_commission !== '' ? Number(this.form.minimum_commission) : null;

			this.isSubmitting = true;
			const payload = {
				product_ids: this.selectedProducts,
				commission_config: {
					type: this.form.type,
					value: Number(this.form.value),
					impact: this.form.impact,
					minimum_commission: minimum,
				},
			};

			const callback = (response) => {
				const product = response.data;
				this.isSubmitting = false;
				this.$notify({
					group: 'products',
					title: 'Bulk Commission',
					text: response?.message ?? 'Commission settings updated',
					type: 'success',
				});
				console.log('respData: ', product);
				this.$emit('success', product);
			};
			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				// eslint-disable-next-line
				console.log(msg);
				this.isSubmitting = false;
				this.$notify({
					group: 'products',
					title: 'Bulk Commission',
					text: msg || 'Failed to update commissions',
					type: 'error',
				});
			};
			bulkUpdateCommission(payload, callback, errorCallback);
		},
	},
};
</script>

<style scoped>
.alert-info {
	display: flex;
	align-items: center;
}
.alert-info .material-icons {
	vertical-align: middle;
}
</style>
